/**
 * BaseAccordion Component
 *
 * The core logic of our custom accordion. This component provides smooth animation and dynamic height adjustment.
 * This component serves as the base for all accordion variants in the application.
 *
 * Key features:
 * - Smooth height animations using react-spring
 * - Dynamic content height adjustment using ResizeObserver
 * - Support for controlled and uncontrolled states
 * - Customizable header rendering
 * - Support for non-collapsible panels
 *
 * ScrollContext Integration:
 * - Each accordion registers itself with ScrollContext using a unique contextId
 * - Provides an API for ScrollContext to:
 *   1. Access individual panels
 *   2. Open/close panels programmatically
 *   3. Access panel header elements for scrolling
 * - This enables external control of accordion behavior through ScrollContext
 */

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ReactNode,
  useCallback,
} from 'react'
import { useSpring, animated } from '@react-spring/web'
import { useScroll } from '../../context/ScrollContext'
import { useLocation } from 'react-router-dom'

// Type definitions for accordion context
interface PanelApi {
  isActive: boolean
  toggle: () => void
  getHeaderElement: () => HTMLElement | null
}

interface AccordionContextType {
  activeKeys: string[]
  handleToggle: (eventKey: string) => void
  activateAccordion: (eventKey: string) => Promise<void>
  registerAccordion: (key: string, ref: PanelApi) => void
  unregisterAccordion: (key: string) => void
  isRestoringState: boolean
}

interface HeaderRenderProps {
  isActive: boolean
  onToggle: () => void
  noCollapse: boolean
}

interface BaseAccordionProps {
  children: ReactNode
  id: string
  defaultActiveKey?: string | string[] | null
  contextIdGenerated?: (id: string) => void
}

interface BasePanelProps {
  eventKey?: string
  children: ReactNode
  renderHeader: (props: HeaderRenderProps) => ReactNode
  noCollapse?: boolean
}

interface BasePanelRef {
  tryCollapse: () => void
}

// Create a context to manage the accordion's state across components
const AccordionContext = createContext<AccordionContextType | null>(null)

// Export the context and a custom hook to access it
export const useAccordionContext = () => {
  const context = useContext(AccordionContext)

  if (!context) {
    throw new Error('useAccordionContext must be used within a BaseAccordion')
  }

  return context
}

/**
 * Main Accordion container that manages the state of all panels.
 */
const BaseAccordion: React.FC<BaseAccordionProps> = ({
  children,
  id,
  defaultActiveKey = null,
  contextIdGenerated = () => {},
}) => {
  const location = useLocation()
  const { registerAccordionContext, registerAccordionState, isNavigatingBack } =
    useScroll()

  const [contextId] = useState(() => {
    if (!id) {
      throw new Error(
        'ID prop is required for BaseAccordion. This is necessary for navigation state persistence. ' +
          "Please provide a unique ID that represents this accordion's position in the UI hierarchy. " +
          'Example: <SimpleAccordion id="client_settings_accordion">',
      )
    }

    const generatedId = `${location.pathname}_${id}`

    contextIdGenerated(generatedId)

    return generatedId
  }) // Initialize activeKeys with saved state or default
  const [activeKeys, setActiveKeys] = useState<string[]>(() => {
    // Check if we have saved state in history
    const savedState = (window.history.state as any)?.navigationInfo
      ?.accordionStates?.[contextId]

    if (savedState) {
      return savedState
    }

    // Otherwise use defaultActiveKey or initialize from children
    const initialKeys =
      defaultActiveKey ||
      React.Children.map(children, (child: any) => child.props.eventKey) ||
      []

    return initialKeys
  })
  const accordionsRef = useRef(new Map<string, PanelApi>())

  // Function to programmatically activate/expand a panel
  const activateAccordion = useCallback(async (eventKey: string) => {
    setActiveKeys((prev) =>
      prev.includes(eventKey) ? prev : [...prev, eventKey],
    )

    return new Promise<void>((resolve) => setTimeout(resolve, 300))
  }, [])

  // Register this accordion context when mounted
  useEffect(() => {
    if (contextId) {
      const contextApi = {
        getAccordion: (key: string) => {
          return accordionsRef.current.get(key) || null
        },
        activateAccordion, // Use the same function
        activateMultipleAccordions: async (keys: string[]) => {
          setActiveKeys(keys)

          return Promise.resolve()
        },
      }

      registerAccordionContext(contextId, contextApi)
    }
  }, [contextId, registerAccordionContext, activateAccordion])

  // Function to handle toggling of panels - now removes from activeKeys when closing
  const handleToggle = (eventKey: string) => {
    setActiveKeys((prev) => {
      const newKeys = prev.includes(eventKey)
        ? prev.filter((key) => key !== eventKey)
        : [...prev, eventKey]

      // Move registerAccordionState to after the state update
      setTimeout(() => {
        registerAccordionState(contextId, newKeys)
      }, 0)

      return newKeys
    })
  }

  const contextValue: AccordionContextType = {
    activeKeys,
    handleToggle,
    activateAccordion,
    registerAccordion: (key: string, ref: PanelApi) => {
      accordionsRef.current.set(key, ref)
    },
    unregisterAccordion: (key: string) => {
      accordionsRef.current.delete(key)
    },
    isRestoringState: isNavigatingBack,
  }

  return (
    <AccordionContext.Provider value={contextValue}>
      {children}
    </AccordionContext.Provider>
  )
}

/**
 * Individual accordion panel that handles its own animations and content rendering.
 */
const BasePanel = forwardRef<BasePanelRef, BasePanelProps>(
  ({ eventKey = '1', children, renderHeader, noCollapse = false }, ref) => {
    // Access the accordion context
    const context = useContext(AccordionContext)

    if (!context) {
      throw new Error('BasePanel must be used within a BaseAccordion')
    }

    const {
      activeKeys,
      handleToggle,
      registerAccordion,
      unregisterAccordion,
      isRestoringState,
    } = context
    const contentRef = useRef<HTMLDivElement>(null)
    const observerRef = useRef<ResizeObserver | null>(null)
    const headerRef = useRef<HTMLDivElement>(null)

    // Initialize isActive based on activeKeys only
    const [isActive, setIsActive] = useState(() =>
      activeKeys.includes(eventKey),
    )
    const lastActive = useRef(isActive)
    const lastDynamicHeight = useRef(0.0)

    // Update isActive when activeKeys changes
    useEffect(() => {
      setIsActive(activeKeys.includes(eventKey))
    }, [activeKeys, eventKey])

    // Custom function to handle panel toggling
    const handlePanelToggle = () => {
      if (noCollapse) return
      if (contentRef.current) {
        const parentElement = contentRef.current.parentElement

        if (parentElement) {
          parentElement.style.overflow = 'hidden'
        }
      }

      lastActive.current = isActive
      handleToggle(eventKey)
      setIsActive(!isActive)
    }

    // Initialize react-spring animation
    const [style, api] = useSpring(() => ({
      height: 0,
      opacity: 0,
      config: {
        tension: 300,
        friction: 20,
        duration: 200,
      },
    }))

    // Add this effect for initial render display state
    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.style.display = isActive ? 'block' : 'none'
      }
      /* eslint-disable-next-line */
    }, []) // Empty dependency array for first render only

    /**
     * Handles measurement and animation of panel content height.
     * Uses ResizeObserver to track content size changes and updates
     * animation accordingly.
     */
    const measureHeight = (): number => {
      const element = contentRef.current

      if (!element) return 0
      let totalHeight = element.getBoundingClientRect().height

      Array.from(element.children).forEach((child) => {
        const style = window.getComputedStyle(child)

        totalHeight +=
          parseFloat(style.marginTop) + parseFloat(style.marginBottom)
      })

      return totalHeight
    }

    const updateHeightToggle = (
      lastActiveValue: boolean,
      nowActiveValue: boolean,
    ) => {
      if (lastActiveValue === false && nowActiveValue === true) {
        if (contentRef.current) {
          contentRef.current.style.display = 'block'
        }

        const height = measureHeight()

        api.start({
          height,
          opacity: 1,
          immediate: isRestoringState,
          onRest: () => {
            if (contentRef.current) {
              contentRef.current.style.display = 'block'
              contentRef.current.style.pointerEvents = 'auto'
              const parentElement = contentRef.current.parentElement

              if (parentElement) {
                parentElement.style.overflow = 'visible'
              }
            }
          },
        })
      } else if (lastActiveValue === true && nowActiveValue === false) {
        api.start({
          height: 0,
          opacity: 0,
          immediate: isRestoringState,
          onRest: () => {
            if (contentRef.current) {
              contentRef.current.style.display = 'none'
              contentRef.current.style.pointerEvents = 'none'
              const parentElement = contentRef.current.parentElement

              if (parentElement) {
                parentElement.style.overflow = 'visible'
              }
            }
          },
        })
      }
    }

    const updateHeightDynamically = () => {
      if (isActive) {
        const height = measureHeight()

        if (Math.abs(height - lastDynamicHeight.current) < 1) {
          lastDynamicHeight.current = height

          return
        }

        if (contentRef.current) {
          const parentElement = contentRef.current.parentElement

          if (parentElement) {
            parentElement.style.overflow = 'hidden'
          }
        }

        api.start({
          height,
          opacity: 1,
          onRest: () => {
            if (contentRef.current) {
              const parentElement = contentRef.current.parentElement

              if (parentElement) {
                parentElement.style.overflow = 'visible'
              }
            }
          },
        })

        lastDynamicHeight.current = height
      } else {
        lastDynamicHeight.current = -1
      }
    }

    useLayoutEffect(() => {
      if (!contentRef.current) return
      updateHeightToggle(lastActive.current, isActive)
      /* eslint-disable-next-line */
    }, [isActive, api, isRestoringState, eventKey])

    useEffect(() => {
      // Create observer once
      observerRef.current = new ResizeObserver(() => {
        window.requestAnimationFrame(() => updateHeightDynamically())
      })

      // Function to observe direct element children safely
      const observeElements = () => {
        if (!contentRef.current) {
          console.warn('BaseAccordion: contentRef is null in observeElements')

          return
        }

        Array.from(contentRef.current.children).forEach((element) => {
          if (element instanceof Element) {
            try {
              observerRef.current?.observe(element)
            } catch (e) {
              console.error(
                'BaseAccordion: Error observing element:',
                element,
                e,
              )
            }
          } else {
            console.warn(
              'BaseAccordion: Skipping observation of non-Element child:',
              element,
            )
          }
        })
      }

      let mutationObserver: MutationObserver | null = null

      // Add a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (contentRef.current && observerRef.current) {
          // First observe the container itself
          observerRef.current.observe(contentRef.current)

          // Then observe key children that might resize
          observeElements()

          // Add a mutation observer to detect new elements
          mutationObserver = new MutationObserver((mutations) => {
            let needsReobserve = false

            mutations.forEach((mutation) => {
              if (
                mutation.type === 'childList' ||
                mutation.type === 'attributes'
              ) {
                needsReobserve = true
              }
            })

            if (needsReobserve && observerRef.current && contentRef.current) {
              // Disconnect and reconnect to catch all elements
              observerRef.current.disconnect()
              observerRef.current.observe(contentRef.current)
              observeElements()
            }
          })

          mutationObserver.observe(contentRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
          })
        }
      }, 50)

      return () => {
        clearTimeout(timer)
        if (mutationObserver) {
          mutationObserver.disconnect()
        }

        if (observerRef.current) {
          observerRef.current.disconnect()
        }
      }
      /* eslint-disable-next-line */
    }, [isActive])

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      tryCollapse: () => {
        if (isActive && !noCollapse) {
          handlePanelToggle()
        }
      },
    }))
    // Register this panel with the accordion context
    useEffect(() => {
      const panelApi: PanelApi = {
        isActive,
        toggle: handlePanelToggle,
        getHeaderElement: () => headerRef.current,
      }

      registerAccordion(eventKey, panelApi)

      return () => unregisterAccordion(eventKey)
      /* eslint-disable-next-line */
    }, [eventKey, isActive])

    return (
      <>
        <div data-name="ref={headerRef}" ref={headerRef}>
          {renderHeader({
            isActive,
            onToggle: handlePanelToggle,
            noCollapse,
          })}{' '}
        </div>
        {/* @ts-ignore - animated.div typing issue with react-spring, functionality preserved */}
        <animated.div
          data-name="animated.div"
          style={{ ...style, overflow: 'hidden' }}
        >
          <div data-name="ref={contentRef}" ref={contentRef}>
            {children}
          </div>
        </animated.div>
      </>
    )
  },
)

BasePanel.displayName = 'BasePanel'

// Create the compound component
const BaseAccordionCompound = BaseAccordion as typeof BaseAccordion & {
  Panel: typeof BasePanel
}

BaseAccordionCompound.Panel = BasePanel

export default BaseAccordionCompound
